import { Button, Input, InputOTP, InputOTPGroup, InputOTPSlot, Spinner } from "@aidkitorg/component-library";
import { useContext, useRef, useState, useEffect } from "react";
import { usePost } from "../API";
import { CandidateChooser } from "../Components/CandidateChooser";
import InterfaceContext, { AuthContext } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse, saveKeyToCookieAndRelocateToUrl } from "../Util";
import { QuestionProps } from "./Props";
import * as v0 from "@aidkitorg/types/lib/survey";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useCookies } from "react-cookie";
import { trySetPortalToken, getPortalToken } from "../Hooks/PortalSession";
import { ApplicationStateContext } from "../Contexts/ApplicationStateContext";
import { buildResumeInfoToLoad } from "./ContactConfirmation";
import { validateContact } from "../utils/contactValidation";

/**
 * Whether the server should own the whole change. Resume mode's other path only re-points `.access` and
 * expects the client to write the value, which this component never does, so a changed contact has to go
 * through confirmContact. An unchanged one has nothing to write, and asserting it anyway would turn an
 * ordinary login into a value write plus an `.access` swap and a portal reissue — on an app whose stored
 * contact can legitimately differ from the one logged in with, since `.access` rows exist for every
 * contact ever confirmed on any field.
 */
export function shouldSendConfirmContact(params: { mode: v0.VerifiedContact['mode'], contactChanged: boolean }) {
  return params.mode !== 'resume' || params.contactChanged;
}

/**
 * The info to look candidates up with. Candidate lookup reads "the app holds attestations the caller
 * doesn't" as evidence of an earlier session, so the caller must claim exactly what is stored on the app
 * and nothing else — any difference in either direction announces a resume that isn't happening.
 */
export function infoForCandidateLookup(params: {
  info: Record<string, string | undefined>,
  valueField: string,
  attestationField: string,
  prefix: string,
  attestation: string,
  /** True when confirmContact was sent, so the attestation below is on the app. */
  serverWroteAttestation: boolean,
}): Record<string, string | undefined> {
  if (!params.serverWroteAttestation) return params.info;
  return {
    ...params.info,
    [params.attestationField]: params.prefix + params.attestation,
    [params.valueField + '_confirmed_info_']: JSON.stringify({ field: params.attestationField }),
  };
}

/**
 * Whether this question is holding contact entry that hasn't been confirmed. Nothing is persisted until a
 * code is confirmed, so the value in `info` can't reveal any of these states, and completeness has to be
 * told. Covers re-editing a confirmed contact, an outstanding code, and — the case Optional would
 * otherwise let you walk past — a fresh contact typed but never confirmed.
 */
export function hasUnconfirmedContactEntry(params: {
  editing: boolean,
  confirmed: boolean,
  challengeOutstanding: boolean,
  contactValue: string,
  storedValue: string | undefined,
}): boolean {
  if (params.editing || params.challengeOutstanding) return true;
  // Compared against what's stored so a seeded-but-unconfirmed value doesn't block on load, before the
  // applicant has touched anything.
  return !params.confirmed && params.contactValue !== (params.storedValue || '');
}

/**
 * Self-contained contact verification: renders its own input and persists nothing until a one-time code
 * is confirmed. Successor to the TextEntry + ContactConfirmation pair.
 */
export function VerifiedContact(props: QuestionProps) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const authContext = useContext(AuthContext);
  const { applicationStateChanged } = useContext(ApplicationStateContext);

  // /p/* link key and screener auth_token cookies feed outerAuthToken (page identity).
  const linkKeyCookieName = encodeURIComponent('key:' + window.location.pathname);
  const [linkKeyCookies] = useCookies([linkKeyCookieName]);
  const linkKey: string | undefined = linkKeyCookies[linkKeyCookieName];
  const [authTokenCookies] = useCookies(['auth_token']);
  const authTokenCookie: string | undefined = authTokenCookies['auth_token'];
  const [, setCookie] = useCookies(['portal']);

  const sendConfirmation = usePost("/confirm/phone");
  const checkCode = usePost("/confirm/phone_code");
  const token = useRef('');
  const resume = usePost("/subsurvey/resume", { token: () => token.current });
  const getApplicantCandidates = usePost("/applicant/get_candidates", { token: () => token.current });

  const metadata = safeParse(props['Metadata'] || '{}') as Omit<v0.VerifiedContact, 'content'> & { contact_field?: string };
  const mode: v0.VerifiedContact['mode'] = metadata.mode || 'confirm';
  // The attestation lives in its own field — the one downstream conditions and comms gate on.
  const valueField = props["Target Field"]!;
  const attestationField = metadata.confirmationField || (valueField + '_confirmation');
  // Required in Distro, but `metadata` is parsed JSON — tolerate it missing rather than mis-typing.
  const contactType: 'email' | 'phone_number' | undefined = metadata.contactType;

  // The shared function rather than the hook, since this component seeds and resets its own state.
  const validate = (raw: string) => validateContact({
    contactType,
    value: raw,
    messages: {
      invalidEmail: L.questions.textentry.must_be_a_valid_email,
      invalidPhone: L.questions.textentry.please_enter_valid_us_number,
      invalidPhoneOrEmail: L.questions.textentry.must_be_a_valid_phone_or_email,
    },
  });
  // Seeding from stored info must carry the message too: an older-format value fails validation and
  // disables Send, so without this the applicant faces a dead button with no explanation.
  const seedError = (raw: string) => (raw ? validate(raw).error : '');

  const [contactValue, setContactValue] = useState(props.info[valueField] || '');
  const [contactValidationError, setContactValidationError] = useState(() => seedError(props.info[valueField] || ''));
  const contactIsValid = !!contactValue && !validate(contactValue).error;
  const isEmail = contactType === 'email' ? true : contactType === 'phone_number' ? false : (contactValue || '').includes('@');
  const prefix = (isEmail ? 'email' : 'phone') + ':';

  const formattedContent = useModularMarkdown({ content: props[languageContent(context.lang)] || '', info: props.info });
  const sendButtonLabel = metadata?.customSendButtonText?.[context.lang]
    || metadata?.customSendButtonText?.en
    || L.questions.contact_confirmation.send_confirmation_code;
  const consentSource = isEmail ? metadata.customEmailConsentText : metadata.customSMSConsentText;
  const consentText = useModularMarkdown({
    content: consentSource ? (consentSource[context.lang] || consentSource.en || '') : '',
    info: props.info,
  });
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [editing, setEditing] = useState(false);
  const checking = useRef(false);
  // Everything from code-submit to persist (checkCode → cross-app propagation → persist) is one state.
  const busy = checkingCode || verifying;

  // Candidate chooser (resume mode only).
  const [showCandidates, setShowCandidates] = useState([] as Awaited<ReturnType<typeof getApplicantCandidates>>['candidates']);
  const [startNewToken, setStartNewToken] = useState('');
  const [homePageLink, setHomePageLink] = useState('');

  const [confirmed, setConfirmed] = useState(!!props.info[attestationField]);
  useEffect(() => {
    setConfirmed(!!props.info[attestationField]);
  }, [props.info[attestationField]]);

  // The cleanup covers unmount too, so a question that disappears mid-entry can't leave the survey stuck.
  const unconfirmedEntry = hasUnconfirmedContactEntry({
    editing, confirmed, challengeOutstanding: !!challengeToken, contactValue,
    storedValue: props.info[valueField],
  });
  useEffect(() => {
    if (!unconfirmedEntry) return;
    props.setQuestionBlocked?.(props.Question, true);
    return () => props.setQuestionBlocked?.(props.Question, false);
  }, [unconfirmedEntry, props.Question, props.setQuestionBlocked]);

  const allowRedirect = props.Viewer !== 'screener'
    && !window.location.pathname.startsWith('/config')
    && !window.location.pathname.startsWith('/p/');

  // setTimeout staggers the writes so back-to-back setInfoKey calls don't clobber each other.
  const persistConfirmed = (attestation: string) => {
    // Now, not on props.info: the attestation lands a tick later and the form would flash in between.
    setConfirmed(true);
    // Not just in onChange: an imported value re-confirmed without retyping would otherwise be written
    // back as-is, over the canonical value the server just stored.
    props.setInfoKey(valueField, validate(contactValue).value, true, false);
    setTimeout(() => {
      props.setInfoKey(attestationField, prefix + attestation, true, false);
      setTimeout(() => {
        // Points at the attestation field, not the value: ownership checks expect a token to decrypt.
        props.setInfoKey(valueField + '_confirmed_info_', JSON.stringify({ field: attestationField }), true, false);
      }, 0);
    }, 0);
  };

  const doResume = async (params: {
    ingested: boolean, resumptionToken: string, confirmationToken: string, decision?: "new" | "resume", isNewApp: boolean,
  }) => {
    // From the chooser, doCheckCode's `finally` already cleared it and the send-code form would show
    // through until the resume lands.
    setVerifying(true);
    try {
      token.current = params.resumptionToken;
      if (params.decision === 'resume' && allowRedirect) {
        alert(L.questions.contact_confirmation.previous_application_found);
      }
      if (params.ingested && allowRedirect) {
        // Matches ContactConfirmation, so programs resuming to a different subsurvey land correctly.
        const redirectsTo = metadata.ingestedResumesToSubsurvey || 'apply';
        const newURL = `${window.location.protocol}//${window.location.host}/p/${redirectsTo}?key=${token.current}`;
        saveKeyToCookieAndRelocateToUrl(newURL);
        return;
      }
      const previous = await resume({ form_name: 'apply' });
      if (previous?.newToken) token.current = previous.newToken;
      const previousInfo = previous?.info || {};
      const infoToLoad = buildResumeInfoToLoad({ previousInfo, currentInfo: props.info, isNewApp: params.isNewApp });
      if (previous.submitted) {
        props.LoadInfo?.(infoToLoad, token.current, previous.submitted);
        return;
      }
      props.LoadInfo?.(infoToLoad, token.current);
      if (!previousInfo[attestationField]) persistConfirmed(params.confirmationToken);
      authContext.setToken(token.current);
    } finally {
      setVerifying(false);
    }
  };

  const doCheckCode = async (fullCode: string) => {
    if (fullCode.length !== 6) {
      setError(L.questions.contact_confirmation.code_is_not_6_digits);
      return;
    }
    if (checking.current) return;
    checking.current = true;
    setCheckingCode(true);
    try {
      const oldValue = props.info[valueField];
      // Compared sanitized, so a case or formatting difference isn't mistaken for a change.
      const contactChanged = !!oldValue && validate(oldValue).value !== validate(contactValue).value;
      const infoWithNewContact = { ...props.info, [valueField]: contactValue, uid: props.uid || props.info.uid };
      // The cookie value is URL-encoded (fernet `=` → %3D) and the backend decodes this field directly.
      const portalToken = getPortalToken();
      const outerAuthToken = props.Viewer === 'screener' ? authTokenCookie : (authContext.token?.() || linkKey);
      // confirmContact hands the whole change to the server in one call: value, .access, the other apps
      // and the session. Nothing is half-applied if the applicant leaves before our own save lands.
      const matched = await checkCode({
        answer: fullCode,
        token: challengeToken!,
        contact_key: valueField,
        uid: props.uid,
        // Authorizes `uid` server-side: a screener's challenge carries no applicant.
        outerAuthToken,
        ...(shouldSendConfirmContact({ mode, contactChanged }) ? {
          confirmContact: {
            confirmationField: attestationField,
            scope: (mode === 'updateAcrossApps' ? 'all_apps' : 'this_app') as 'all_apps' | 'this_app',
            info: infoWithNewContact,
            currentPortalToken: portalToken ? decodeURIComponent(portalToken) : undefined,
            oldContact: oldValue,
          },
        } : {}),
      });
      if (matched.error === 'challenge_expired') {
        setChallengeToken(null); setCode(''); alert(L.questions.contact_confirmation.code_expired); return;
      }
      if (matched.error === 'challenge_incorrect') {
        // Clear so the re-shown inputs are empty and onComplete can fire again on the retry.
        setCode(''); alert(L.questions.contact_confirmation.code_incorrect); return;
      }
      if (matched.error) {
        // The server applied nothing, so say so and leave their session alone — clearing it would log
        // them out with no explanation.
        setChallengeToken(null); setCode(''); alert(L.questions.contact_confirmation.update_contact_failed); return;
      }
      if (!matched.attestation) {
        setChallengeToken(null); setCode(''); authContext.setToken(null); return;
      }

      setVerifying(true);
      setEditing(false);
      setChallengeToken(null);
      setCode('');
      token.current = matched.attestation;
      try {
        if (mode === 'resume') {
          // Entry flow: resolve candidates and resume/start an app.
          const res = await getApplicantCandidates({
            info: infoForCandidateLookup({
              info: infoWithNewContact,
              valueField, attestationField, prefix,
              attestation: matched.attestation,
              serverWroteAttestation: shouldSendConfirmContact({ mode, contactChanged }),
            }),
            mode: 'confirmation',
            outerAuthToken,
          });
          if (res.portalToken) { setCookie('portal', res.portalToken, { path: '/' }); trySetPortalToken(res.portalToken); }
          const { candidates, canStartNewToken, homePageLink: hpl } = res;
          const anyCandidateHasSubsurveys = candidates.some((c) => c.subsurveys?.length);
          if (allowRedirect && candidates.length && (candidates.length > 1 || canStartNewToken || anyCandidateHasSubsurveys)) {
            setStartNewToken(canStartNewToken || '');
            setHomePageLink(hpl || '');
            setShowCandidates(candidates);
            return;
          }
          if (candidates.length === 1 && !canStartNewToken) {
            return await doResume({
              ingested: candidates[0].applicant.kind === 'ingested',
              resumptionToken: candidates[0].resumeToken,
              confirmationToken: matched.attestation,
              decision: res.decision,
              isNewApp: res.decision === 'new',
            });
          }
          persistConfirmed(matched.attestation);
          if (!authContext.token?.()) authContext.setToken(matched.attestation);
          return;
        }

        // A sibling that didn't take is deliberately not shown: this app is fine and the applicant can't
        // do anything about another one. The engine records that case instead.
        if (matched.portalToken) { setCookie('portal', matched.portalToken, { path: '/' }); trySetPortalToken(matched.portalToken); }
        // Persist LAST: this flips `confirmed` (the ✓ view, which hides the loading spinner), so hold
        // it until the reissue above has finished — otherwise the confirm looks done mid-flight.
        persistConfirmed(matched.attestation);
        if (!authContext.token?.()) authContext.setToken(matched.attestation);
      } catch (e) {
        setVerifying(false);
        throw e;
      }
    } finally {
      checking.current = false;
      setCheckingCode(false);
      setVerifying(false);
    }
  };

  const sendCode = async () => {
    if (sendingCode) return;
    setError('');
    setSendingCode(true);
    try {
      const challenges = await sendConfirmation({ phone: contactValue, language: context.lang, sendCodeViaWhatsapp: metadata.sendCodeViaWhatsapp });
      if (challenges && challenges.token) {
        setChallengeToken(challenges.token);
        applicationStateChanged({ type: 'change', questionId: props.Question, update: { validity: { state: 'valid' } } });
      } else {
        applicationStateChanged({
          type: 'change', questionId: props.Question,
          update: { validity: { state: 'invalid', message: L.questions.contact_confirmation.send_confirmation_code_failed } },
        });
        alert(L.questions.contact_confirmation.send_confirmation_code_failed);
      }
    } finally {
      setSendingCode(false);
    }
  };

  // Safe to return empty, unlike Computed: nothing here computes in the background.
  if ((props["Additional Options"] || []).indexOf("Hidden") !== -1) {
    return <></>
  }

  if (showCandidates && showCandidates.length > 0) {
    return <CandidateChooser
      label={<>{L.apply.we_found_some_existing_apps}{startNewToken ? <><br /><span>{L.apply.you_can_also_start_new}</span></> : null}</>}
      candidates={[
        ...(homePageLink ? [{
          buttonLabel: L.questions.contact_confirmation.go_to_home_page,
          onClick: async () => { setShowCandidates([] as any[]); window.location.href = homePageLink; },
        }] : []),
        ...showCandidates.map((c) => ({
          ...c,
          buttonLabel: metadata?.customResumeButtonText?.[context.lang],
          onClick: async () => {
            setShowCandidates([] as any[]);
            await doResume({ ingested: c.applicant.kind === 'ingested', resumptionToken: c.resumeToken, confirmationToken: token.current, isNewApp: false });
          },
        })),
        ...(startNewToken ? [{
          buttonLabel: L.apply.start_new_application,
          onClick: async () => {
            setShowCandidates([] as any[]);
            await doResume({ ingested: false, resumptionToken: startNewToken, confirmationToken: token.current, isNewApp: true });
          },
        }] : []),
      ]}
      onClose={() => { setShowCandidates([] as any[]); setVerifying(false); }}
    />;
  }

  return <fieldset className="max-w-sm">
    <legend>{formattedContent}</legend>
    {/* `verifying` keeps us out of the ✓ view while post-OTP work is still in flight. Re-confirming an
        already-confirmed contact still has the OLD attestation in info, so `confirmed` is true the whole
        time — without this the ✓ view (which has no spinner) appears the moment editing ends. */}
    {confirmed && !editing && !verifying
      ? <div className="mt-2 flex flex-col gap-1">
        {/* Distinct key from the editable input: otherwise React reuses the DOM node and an autofilled
            field keeps its autofill background here, since UA styles beat our disabled colors. */}
        <Input
          key="confirmed"
          label={valueField}
          showLabel={false}
          className="w-full"
          // Match a plain TextEntry; Input's wrapper otherwise defaults wider than every other field.
          wrapperClassName="sm:max-w-xs"
          value={contactValue || props.info[valueField]}
          disabled
          suffix={<span className="text-green-600" aria-hidden="true">✓</span>}
        />
        <span className="text-sm text-green-700">{L.questions.contact_confirmation.contact_has_been_confirmed}</span>
        <div>
          <Button variant="secondary" onClick={() => {
            const stored = props.info[valueField] || contactValue;
            setContactValue(stored);
            setContactValidationError(seedError(stored));
            setChallengeToken(null);
            setCode('');
            setVerifying(false);
            setEditing(true);
          }}>
            {L.questions.textentry.edit}
          </Button>
        </div>
      </div>
      : <div className="mt-2 flex flex-col gap-3">
        <Input
          key="editing"
          label={valueField}
          showLabel={false}
          className="w-full"
          wrapperClassName="sm:max-w-xs"
          type={contactType === 'email' ? 'email' : contactType === 'phone_number' ? 'tel' : 'text'}
          autoComplete={contactType === 'phone_number' ? 'tel' : 'email'}
          placeholder={contactType === 'email' ? 'user@domain.com' : contactType === 'phone_number' ? '555-555-5555' : 'user@domain.com | 555-555-5555'}
          value={contactValue}
          disabled={!!challengeToken || busy}
          error={contactValidationError ? { hasError: true, errorMessage: contactValidationError } : undefined}
          onChange={(e) => { const r = validate(e.target.value); setContactValue(r.value); setContactValidationError(r.error); }}
        />
        {/* One loading state throughout: a mid-flight swap of spinners reads as something going wrong. */}
        {busy
          ? <Spinner size="small" className="p-0 w-auto" />
          : !challengeToken
            ? <div className="flex flex-col gap-2">
              {consentSource ? <div>{consentText}</div> : null}
              <div className="flex items-center gap-2">
                {/* Overlaid on an invisible label; Button's `loading` swaps it out and the button shrinks. */}
                <Button variant="primary" aria-busy={sendingCode} isDisabled={sendingCode || !contactIsValid} onClick={sendCode}>
                  <span className="relative inline-flex items-center justify-center">
                    <span className={sendingCode ? 'invisible' : undefined}>{sendButtonLabel}</span>
                    {sendingCode && <Spinner size="xsmall" className="absolute p-0 w-auto" />}
                  </span>
                </Button>
                {editing &&
                  <Button variant="secondary" onClick={() => {
                    setEditing(false);
                    setContactValue(props.info[valueField] || '');
                    setContactValidationError(seedError(props.info[valueField] || ''));
                    setChallengeToken(null);
                    setCode('');
                  }}>
                    {L.apply.cancel}
                  </Button>}
              </div>
            </div>
            : <div className="flex flex-col gap-2">
              <span>{isEmail
                ? L.questions.contact_confirmation.please_enter_code_from_email
                : L.questions.contact_confirmation.please_enter_code_from_sms}</span>
              <InputOTP
                label={L.apply.confirm_code}
                maxLength={6}
                value={code}
                error={!!error}
                onChange={(value) => { setCode(value); if (error) setError(''); }}
                onComplete={doCheckCode}
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
              <div className="text-sm text-gray-600">{L.questions.contact_confirmation.authentication_code_disclaimer}</div>
              <div className="flex items-center gap-2">
                <Button variant="primary" isDisabled={code.length !== 6} onClick={() => doCheckCode(code)}>
                  {L.apply.confirm_code}
                </Button>
                <Button variant="secondary" onClick={() => { setCode(''); setChallengeToken(null); }}>
                  {L.apply.cancel}
                </Button>
              </div>
            </div>}
      </div>}
  </fieldset>;
}
